"use client";

import { useState } from "react";
import Link from "next/link";
import SectionHeader from "@/components/ui/section-header";
import { ArrowUpRight, Check, Copy } from "@/components/icons";
import { cn } from "@/lib/utils";
import { DOCS, GITHUB_MCP_URL } from "@/lib/constants";
import { DEV_TABS } from "@/components/sections/products/residential-proxies/for-developers/data";
import DevCodeDisplay from "@/components/sections/products/shared/dev-code-display";

// German clone of residential-proxies/for-developers — reuses the exact same
// DEV_TABS (code + language-tab labels like "cURL"/"Python" are
// language-agnostic), only prose and resource-link labels are translated.

const LINKS: { label: string; href: string }[] = [
  { label: "Residential-Proxy-Dokumentation", href: DOCS.residentialProxies },
  { label: "Residential-API-Referenz", href: DOCS.apiPerGbResidential },
  { label: "MCP-Server", href: "/tools/mcp-server" },
  { label: "MCP-Server auf GitHub", href: GITHUB_MCP_URL },
  { label: "Chrome-Erweiterung", href: "/tools/chrome-extension" },
  { label: "Playwright-Proxy-Setup-Anleitung", href: "/blog/playwright-proxy-python-nodejs/" },
  { label: "Browser-Use-Integrationsanleitung", href: "/blog/browser-use-residential-proxies/" },
  { label: "Proxy-Sitzungsverwaltung für KI-Agenten", href: "/blog/proxy-sessions-for-ai-agents/" },
];

export default function ResidentialForDevelopersDe() {
  const [activeTab, setActiveTab] = useState(DEV_TABS[0].id);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (id: string, code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
  };

  return (
    <section
      className="py-section-sm border-border border-b"
      aria-labelledby="residential-for-developers-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 max-w-3xl">
          <SectionHeader
            eyebrow="Für Entwickler gebaut"
            id="residential-for-developers-heading"
            heading="Residential Proxies für Entwickler"
            body="HTTP, HTTPS und SOCKS5 leiten den Datenverkehr Ihres Agenten oder Scrapers über Proxidize Residential-IPs. REST-API und MCP-Server verwalten diese Ressourcen — Zugangsdaten, Rotation, Standort — nicht den Datenverkehr selbst. Browser und Datenextraktion laufen weiterhin in Ihrer eigenen Anwendung."
          />
        </div>

        <div className="border-border bg-card overflow-hidden rounded-2xl border">
          <div
            role="tablist"
            aria-label="Code-Beispiele nach Sprache"
            className="border-border scrollbar-hide flex items-center gap-0.5 overflow-x-auto border-b px-2"
          >
            {DEV_TABS.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`dev-tab-de-${tab.id}`}
                  aria-selected={isActive}
                  aria-controls={`dev-panel-de-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "shrink-0 border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors",
                    isActive
                      ? "border-brand-green text-foreground"
                      : "text-muted-foreground hover:text-foreground border-transparent"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {DEV_TABS.map((tab) => (
            <div
              key={tab.id}
              id={`dev-panel-de-${tab.id}`}
              role="tabpanel"
              aria-labelledby={`dev-tab-de-${tab.id}`}
              className={cn(tab.id !== activeTab && "hidden")}
            >
              <div className="border-border flex items-center justify-between border-b px-5 py-3">
                <span className="text-muted-foreground bg-foreground/5 rounded px-2 py-0.5 font-mono text-xs font-medium">
                  {tab.lang}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(tab.id, tab.code)}
                  className="text-muted-foreground hover:text-foreground -m-2 flex items-center gap-1.5 p-2 text-xs transition-colors"
                  aria-label="Code kopieren"
                >
                  {copiedId === tab.id ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === tab.id ? "Kopiert!" : "Kopieren"}
                </button>
              </div>
              <div className="overflow-x-auto">
                <DevCodeDisplay code={tab.code} language={tab.lang} />
              </div>
            </div>
          ))}
        </div>

        <p className="text-muted-foreground mt-6 max-w-3xl text-sm leading-relaxed">
          Das Land-Targeting wird direkt im Benutzernamen festgelegt, wie oben gezeigt. Stadt, ISP
          und Session-Modus (rotierend oder sticky) werden pro Access Point im Dashboard oder im
          Session Builder konfiguriert — Zugangsdaten dort erzeugen und in eines der Beispiele
          einsetzen.
        </p>

        <p className="text-muted-foreground mt-8 mb-3 text-xs font-bold tracking-wider uppercase">
          Entwickler-Ressourcen
        </p>
        <ul
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          aria-label="Entwickler-Ressourcen"
        >
          {LINKS.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                target={link.href.startsWith("http") ? "_blank" : undefined}
                rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="border-border bg-card hover:border-foreground/20 hover:bg-foreground/[0.02] group flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors"
              >
                <span className="text-foreground text-sm font-medium">{link.label}</span>
                <ArrowUpRight
                  size={14}
                  className="text-muted-foreground group-hover:text-foreground shrink-0 transition-colors"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
